import { ChangeDetectorRef } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { FilterField, FilterSection } from './filter';

/**
 * This cannot safely be called before the AfterViewInit angular lifecycle hook.
 *
 * When attaching a translator, it will assume that the values given for
 * things like filter name are actually translation keys, and will use them as
 * inputs during translation.
 *
 * Provide ChangeDetectorRef to inform the app when new values are set by the
 * translator during the change detection round.
 *
 * IMPORTANT: Calling this function will create observable subscriptions.  You
 * will need to clean them up during the OnDestroy lifecycle hook of your
 * filter component.  The subscriptions are provided on the return value of
 * this function.
 */
export function subscribeFilterFieldToTranslator(
  field: FilterField<any>,
  translateService: {
    instant: (key: string | Array<string>) => string | any;
    stream: (key: string | Array<string>) => Observable<string | any>;
  },
  changeDetector: ChangeDetectorRef,
): Subscription[] {
  const nameKey = field.name;
  field.name = translateService.instant(nameKey);
  const sub = translateService.stream(nameKey).subscribe((translatedName) => {
    field.name = translatedName;
    changeDetector.detectChanges();
  });
  return [sub];
}

/**
 * This cannot safely be called before the AfterViewInit angular lifecycle hook.
 *
 * @see subscribeFilterFieldToTranslator as this behaves similarly
 *
 * The `subscribeChildFields` parameter can be set to `true` to hook up all of
 * the children of this section to receive translation updates.
 */
export function subscribeFilterSectionToTranslator(
  section: FilterSection,
  translator: {
    stream: (key: string | Array<string>) => Observable<string | any>;
    instant: (key: string | Array<string>) => string | any;
  },
  cd: ChangeDetectorRef,
  subscribeChildFields: boolean,
): Subscription[] {
  const nameKey = section.title;
  section.title = translator.instant(nameKey!);
  const sub = translator.stream(nameKey!).subscribe((translatedName) => {
    section.title = translatedName;
    cd.detectChanges();
  });

  const subs = [sub];
  if (subscribeChildFields) {
    section.fields.forEach((field) => {
      subs.push(...field.subscribeToTranslator(translator, cd));
    });
  }
  return subs;
}
